import shared from "../Shared/shared.module.css";
import { IconSchool, IconPlus, IconTrash, IconAlertCircle, IconChevronsLeft, IconArrowRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { talentEducation, talentProfile } from "../../../services/talentCalls";
import { errorHandler } from "../../../utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";

const emptyForm = { schoolName: "", degree: "", fieldOfStudy: "", startDate: "", endDate: "" };

const Stage3 = () => {
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(false);
  const [errors, setErrors] = useState({});
  const [forms, setForms] = useState([{ ...emptyForm }]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const extractEducationData = () => {
    try {
      const extractedDataString = localStorage.getItem("extractedResumeData");
      if (!extractedDataString) return false;
      const extractedData = JSON.parse(extractedDataString);
      if (extractedData?.education && Array.isArray(extractedData.education)) {
        const formattedData = extractedData.education.map((eduItem) => {
          const str = eduItem || "";
          const dateMatch = str.match(/(\d{4})\s*[-–]\s*(\d{4}|Present)/);
          const startYear = dateMatch ? parseInt(dateMatch[1]) : null;
          const endYear = dateMatch ? (dateMatch[2] === "Present" ? null : parseInt(dateMatch[2])) : null;
          const cleanStr = str.replace(/(\d{4})\s*[-–]\s*(\d{4}|Present)/, "").trim();
          let school = "", degree = "", fieldOfStudy = "";
          if (cleanStr.includes("–")) {
            const parts = cleanStr.split("–");
            if (parts.length >= 2) {
              const part1 = parts[0].trim();
              const part2 = parts[1].trim();
              const isSchool1 = /(University|College|Institute|School|Academy)/i.test(part1);
              const isSchool2 = /(University|College|Institute|School|Academy)/i.test(part2);
              if (isSchool1 && !isSchool2) { school = part1; degree = part2; }
              else if (isSchool2 && !isSchool1) { degree = part1; school = part2; }
              else { degree = part1; school = part2; }
            }
          } else if (cleanStr.includes("|")) {
            school = cleanStr.split("|")[0]?.trim() || "";
          } else if (cleanStr.includes(" in ")) {
            const inIndex = cleanStr.indexOf(" in ");
            const atIndex = cleanStr.indexOf(" at ");
            if (atIndex > -1) {
              degree = cleanStr.substring(0, inIndex)?.trim() || "";
              fieldOfStudy = cleanStr.substring(inIndex + 4, atIndex)?.trim() || "";
              school = cleanStr.substring(atIndex + 4)?.trim() || "";
            } else {
              degree = cleanStr.substring(0, inIndex)?.trim() || "";
              fieldOfStudy = cleanStr.substring(inIndex + 4)?.trim() || "";
            }
          } else {
            degree = cleanStr;
          }
          return {
            schoolName: school,
            degree,
            fieldOfStudy,
            startDate: startYear ? new Date(startYear, 0, 1) : null,
            endDate: endYear ? new Date(endYear, 11, 31) : null,
          };
        });
        if (formattedData.length > 0) {
          setForms(formattedData);
        } else {
          getUserProfile();
        }
        return true;
      }
    } catch (_) {}
    return false;
  };

  const getUserProfile = async () => {
    setLoading(true);
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent) setApiData(res?.data?.data?.talent?.educations);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hasResumeData = extractEducationData();
    if (!hasResumeData) getUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(apiData) && apiData.length > 0 && forms[0].schoolName === "") {
      setForms(
        apiData.map((item) => ({
          schoolName: item.schoolName || "",
          degree: item.degree || "",
          fieldOfStudy: item.fieldOfStudy || "",
          startDate: item.startDate ? new Date(item.startDate) : null,
          endDate: item.endDate ? new Date(item.endDate) : null,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData]);

  const handleChangeDate = (i, name, date) => {
    const newForms = [...forms];
    newForms[i][name] = date;
    setForms(newForms);
    const newErrors = { ...errors };
    delete newErrors[`form_${i}_${name}`];
    setErrors(newErrors);
  };

  const handleChange = (i, e) => {
    const { name, value } = e.target;
    if (/^[a-zA-Z\s,.-]*$/.test(value) || value === "") {
      const newForms = [...forms];
      newForms[i][name] = value;
      setForms(newForms);
      const newErrors = { ...errors };
      delete newErrors[`form_${i}_${name}`];
      setErrors(newErrors);
    }
  };

  const validateForms = () => {
    let isValid = true;
    const newErrors = {};
    forms.forEach((form, index) => {
      if (!form.schoolName.trim()) { isValid = false; newErrors[`form_${index}_schoolName`] = "School Name is required."; }
      if (!form.degree.trim()) { isValid = false; newErrors[`form_${index}_degree`] = "Degree is required."; }
      if (!form.fieldOfStudy.trim()) { isValid = false; newErrors[`form_${index}_fieldOfStudy`] = "Field of Study is required."; }
      if (!form.startDate) { isValid = false; newErrors[`form_${index}_startDate`] = "Start Date is required."; }
      if (!form.endDate) { isValid = false; newErrors[`form_${index}_endDate`] = "End Date is required."; }
      else if (new Date(form.startDate) > new Date(form.endDate)) { isValid = false; newErrors[`form_${index}_endDate`] = "End Date cannot be before Start Date."; }
    });
    setErrors(newErrors);
    return isValid;
  };

  const submit = async () => {
    if (!validateForms()) return;
    const formattedForms = forms.map((form) => ({
      schoolName: form.schoolName,
      degree: form.degree,
      fieldOfStudy: form.fieldOfStudy,
      startDate: form.startDate instanceof Date ? form.startDate.toISOString().split("T")[0] : form.startDate,
      endDate: form.endDate instanceof Date ? form.endDate.toISOString().split("T")[0] : form.endDate,
    }));
    try {
      setLoad(true);
      await talentEducation({ educations: formattedForms });
      navigate({ pathname: "/talent-onboarding", search: "stage=4" });
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setLoad(false);
    }
  };

  return (
    <>
      {(loading || load) && <FullScreenLoader />}
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconSchool size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Educational Background</div>
            <div className={shared.panelSubtitle}>Tell us about your academic qualifications</div>
          </div>
        </div>
        {forms.length < 5 && (
          <div className={shared.panelHeroActionWrap}>
            <div className={shared.panelHeroAction} title="Add another qualification" onClick={() => setForms([...forms, { ...emptyForm }])}>
              <IconPlus size={18} />
            </div>
            <div className={shared.panelHeroActionLabel}>Add Qualification</div>
          </div>
        )}
      </div>

      <div className={shared.panelBody}>
        {forms.map((item, index) => (
          <div className={shared.entryBlock} key={index}>
            {index > 0 && (
              <button
                type="button"
                className={shared.btnRemoveEntry}
                onClick={() => {
                  const updated = [...forms];
                  updated.splice(index, 1);
                  setForms(updated);
                }}
              >
                <IconTrash size={12} /> Remove
              </button>
            )}

            <div className={`${shared.formRow} ${shared.formRowFull}`}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>School Name<span className={shared.req}>*</span></label>
                <input className={shared.formInput} type="text" placeholder="University Name" name="schoolName" value={item.schoolName} onChange={(e) => handleChange(index, e)} />
                {errors[`form_${index}_schoolName`] && (
                  <div className={shared.fieldErr}><IconAlertCircle size={13} /> {errors[`form_${index}_schoolName`]}</div>
                )}
              </div>
            </div>

            <div className={shared.formRow}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Degree<span className={shared.req}>*</span></label>
                <input className={shared.formInput} type="text" placeholder="Bachelor of Science" name="degree" value={item.degree} onChange={(e) => handleChange(index, e)} />
                {errors[`form_${index}_degree`] && (
                  <div className={shared.fieldErr}><IconAlertCircle size={13} /> {errors[`form_${index}_degree`]}</div>
                )}
              </div>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Field of Study<span className={shared.req}>*</span></label>
                <input className={shared.formInput} type="text" placeholder="Computer Science" name="fieldOfStudy" value={item.fieldOfStudy} onChange={(e) => handleChange(index, e)} />
                {errors[`form_${index}_fieldOfStudy`] && (
                  <div className={shared.fieldErr}><IconAlertCircle size={13} /> {errors[`form_${index}_fieldOfStudy`]}</div>
                )}
              </div>
            </div>

            <div className={shared.formRow}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Start Date<span className={shared.req}>*</span></label>
                <DatePicker
                  selected={item.startDate}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="dd-mm-yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  maxDate={item.endDate || new Date()}
                  onChange={(date) => handleChangeDate(index, "startDate", date)}
                  className={shared.formInput}
                  wrapperClassName="w-full"
                />
                {errors[`form_${index}_startDate`] && (
                  <div className={shared.fieldErr}><IconAlertCircle size={13} /> {errors[`form_${index}_startDate`]}</div>
                )}
              </div>
              <div className={shared.formField}>
                <label className={shared.formLabel}>End Date<span className={shared.req}>*</span></label>
                <DatePicker
                  selected={item.endDate}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="dd-mm-yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  minDate={item.startDate}
                  onChange={(date) => handleChangeDate(index, "endDate", date)}
                  className={shared.formInput}
                  wrapperClassName="w-full"
                />
                {errors[`form_${index}_endDate`] && (
                  <div className={shared.fieldErr}><IconAlertCircle size={13} /> {errors[`form_${index}_endDate`]}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={shared.panelFooter}>
        <button type="button" className={shared.btnBack} onClick={() => navigate("?stage=2")}>
          <IconChevronsLeft size={16} /> Back
        </button>
        <div className={shared.footerActions}>
          <button type="button" className={shared.btnPrimary} onClick={submit} disabled={load}>
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Stage3;
